import React, { useEffect } from "react";
import MainLayout from "../../../layouts/MainLayout";
import { useNavigate, useParams } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useNotification from "../../../hooks/useNotification";
import useLoader from "../../../hooks/useLoader";
import useForm from "../../../hooks/useForm";
import Form from "../../../components/forms/Form";
import { editMovementsFields } from "../../../constants/forms/shipmentFields";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import SectionHeader from "../../../components/forms/SectionHeader";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const EditShipmentsPage = () => {
  const navigate = useNavigate();
  const { shipmentId } = useParams();

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const {
    getByColumn: baseServiceGetById,
    put: baseServiceUpdate,
    formErrors,
  } = useBaseService(navigate);
  const { formData, handleChange, formatData } = useForm(
    setDefaultFieldValues(editMovementsFields),
    editMovementsFields,
  );
  const queryClient = useQueryClient();

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      "status.name": [], // Supondo que você queira filtrar pelo nome do status
      deleted_at: false,
    },
    "shipment",
  ); // Um prefixo para os filtros na URL

  const {
    data: shipmentData,
    isLoading,
    isError,
    error,
  } = useQueryWithLoader({
    queryKey: ["shipment", shipmentId],
    queryFn: async () => {
      const response = await baseServiceGetById(
        entities.shipments.getById(shipmentId),
      );
      return response.result;
    },
    enabled: !!shipmentId,
  });

  useEffect(() => {
    if (!isLoading && shipmentData) {
      formatData(shipmentData, editMovementsFields);
    }
    if (isError && error) {
      console.log("Erro ao buscar departamento", error);
      showNotification(
        "error",
        `Erro ao buscar departamento: ${error?.message}`,
      );
    }
  }, [error, formatData, isError, isLoading, shipmentData, showNotification]);

  const updateMovementShipment = useMutation({
    mutationFn: async (formData) => {
      showLoader();
      const response = await baseServiceUpdate(
        entities.shipments.update(shipmentId),
        formData,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        navigate("/aluguel/carregamentos");
        queryClient.invalidateQueries({
          queryKey: ["shipments", filters, pagination],
        });
      }
      hideLoader();
    },
    onError: (error) => {
      console.error("onError ", error);
      hideLoader();
    },
  });

  const handleSubmit = async () => {
    try {
      await updateMovementShipment.mutateAsync(formData);
    } catch (error) {
      console.error("Erro ao enviar carregamento:", {
        status: error.status,
        message: error.message,
      });
    }
  };

  const handleBack = () => {
    navigate(`/aluguel/carregamentos`);
  };

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Editar Carregamento do Movimento"
        showBackButton={true}
        backUrl={`/aluguel/carregamentos`}
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Editar"
          textLoadingSubmit="Editando..."
          handleBack={handleBack}
        >
          {() => {
            const renderedSections = new Set();

            return editMovementsFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={() => {}}
                    getSelectedValue={() => {}}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditShipmentsPage;
